import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Stack,
  Grid,
  Button,
  Loader,
  Alert,
  Box,
  Paper,
  Affix,
  Transition,
  Group,
  ActionIcon,
  Badge,
  Text,
  Title,
  Tabs
} from '@mantine/core';
import { IconArrowLeft, IconExclamationCircle, IconArrowUp, IconEye, IconPackage, IconTruck, IconReceipt } from '@tabler/icons-react';
import { useWindowScroll } from '@mantine/hooks';
import { PaymentService, ShipmentService } from '../types';

import ShipmentHeader from '../components/shipment/ShipmentHeader';
import TrackingInformation from '../components/shipment/TrackingInformationDetail';
import DestinationAddress from '../components/shipment/DestinationAddress';
import CostSummary from '../components/shipment/CostSummary';
import ShipmentSummary from '../components/shipment/ShipmentSummary';
import PackagesList from '../components/shipment/PackagesList';

import { Shipment, ShipmentApiResponse } from '../components/shipment/types';
import { mapApiResponseToShipment } from '../components/shipment/utils';


const ShipmentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shipmentData, setShipmentData] = useState<Shipment | null>(null);
  const [scroll, scrollTo] = useWindowScroll();
  const [activeTab, setActiveTab] = useState<string | null>('overview');


  useEffect(() => {
    // Fetch shipment data
    const fetchShipmentData = async () => {
      setIsLoading(true);
      try {
        if (id) {
          const res: any = await ShipmentService.getShipment1(Number(id));

          //check payment status
          if (res.pay_state !== 'CONFIRMED' && res.transactionId) {
            //check payment status again
            const paymentStatus: any = await PaymentService.getPaymentStatus(res.transactionId);
            //if payment status is changed re fetch shipment data
            if (paymentStatus.state == res.pay_state) {
              const updatedShipment = await ShipmentService.getShipment1(Number(id));
              setShipmentData(mapApiResponseToShipment(updatedShipment as unknown as ShipmentApiResponse));
              return;
            }
          }

          // Transform API response to our Shipment interface
          const mappedData = mapApiResponseToShipment(res as unknown as ShipmentApiResponse);
          setShipmentData(mappedData);
        } else {
          throw new Error('Shipment ID is missing');
        }
      } catch (err) {
        setError('Failed to load shipment details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchShipmentData();
  }, [id]);

  if (isLoading) {
    return (
      <Container py="xl" size="lg">
        <Stack align="center" my="xl">
          <Loader size="lg" />
          <Text size="sm" c="dimmed" mt="md">Loading shipment details...</Text>
        </Stack>
      </Container>
    );
  }


  if (error || !shipmentData) {
    return (
      <Container py="xl" size="lg">
        <Alert
          icon={<IconExclamationCircle />}
          title="Error Loading Shipment"
          color="red"
          variant="filled"
          mb="lg"
        >
          {error || 'Shipment not found'}
        </Alert>
        <Button
          component={Link}
          to="/shipments"
          leftSection={<IconArrowLeft size={16} />}
          variant="light"
          fullWidth
          size="md"
        >
          Back to Shipments
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Container py={{ base: "sm", sm: "xl" }} size="lg" className="shipment-detail-page">
        {/* Mobile-First Header Section */}
        <Paper withBorder p={{ base: "sm", sm: "md" }} radius="md" mb="md" bg="gray.0">
          <ShipmentHeader shipment={shipmentData} />

          {/* Quick Status Overview - Mobile Optimized */}
          <Box mt="sm" display={{ base: 'block', sm: 'none' }}>
            <Group justify="space-between" mb="xs">
              <Text size="xs" c="dimmed">Status</Text>
              <Badge
                size="sm"
                color={shipmentData.status === 'delivered' ? 'green' : shipmentData.status === 'shipped' ? 'blue' : 'yellow'}
              >
                {shipmentData.status.charAt(0).toUpperCase() + shipmentData.status.slice(1)}
              </Badge>
            </Group>
            <Group justify="space-between" mb="xs">
              <Text size="xs" c="dimmed">Tracking</Text>
              <Text size="xs" fw={500}>{shipmentData.trackingNumber}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">Packages</Text>
              <Text size="xs" fw={500}>{shipmentData.packageCount} item(s)</Text>
            </Group>
          </Box>
        </Paper>

        {/* Mobile Back Button - Sticky */}
        <Box mb="md" display={{ base: 'block', sm: 'none' }}>
          <Button
            component={Link}
            to="/shipments"
            leftSection={<IconArrowLeft size={16} />}
            variant="light"
            size="sm"
            fullWidth
          >
            Back to All Shipments
          </Button>
        </Box>

        {/* Tab Navigation for Mobile */}
        <Box display={{ base: 'block', md: 'none' }} mb="md">
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List grow>
              <Tabs.Tab value="overview" leftSection={<IconEye size={16} />} />
              <Tabs.Tab value="tracking" leftSection={<IconTruck size={16} />} />
              <Tabs.Tab value="packages" leftSection={<IconPackage size={16} />} />
              <Tabs.Tab value="billing" leftSection={<IconReceipt size={16} />} />
            </Tabs.List>

            <Tabs.Panel value="overview" pt="md">
              <Stack gap="md">
                <CostSummary cost={shipmentData.cost} />
                <DestinationAddress destination={shipmentData.destination} />
                <ShipmentSummary
                  totalWeight={shipmentData.totalWeight}
                  totalValue={shipmentData.totalValue}
                  packageCount={shipmentData.packageCount}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="tracking" pt="md">
              <TrackingInformation shipment={shipmentData} />
            </Tabs.Panel>

            <Tabs.Panel value="packages" pt="md">
              <PackagesList packages={shipmentData.packages} />
            </Tabs.Panel>

            <Tabs.Panel value="billing" pt="md">
              <CostSummary cost={shipmentData.cost} />
            </Tabs.Panel>
          </Tabs>
        </Box>

        {/* Desktop Grid Layout */}
        <Box display={{ base: 'none', md: 'block' }}>
          <Grid gutter="lg">
            {/* Main Content - Left Column */}
            <Grid.Col span={8}>
              <Stack gap="lg">
                {/* Tracking Section */}
                <TrackingInformation shipment={shipmentData} />

                {/* Packages Section */}
                <PackagesList packages={shipmentData.packages} />
              </Stack>
            </Grid.Col>

            {/* Sidebar - Right Column */}
            <Grid.Col span={4}>
              <Stack gap="md">
                {/* Quick Summary */}
                <Paper withBorder p="md" radius="md">
                  <Title order={4} mb="sm">Quick Summary</Title>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Total Weight</Text>
                      <Text size="sm" fw={500}>{shipmentData.totalWeight} kg</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Total Value</Text>
                      <Text size="sm" fw={500}>${shipmentData.totalValue.toFixed(2)}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Packages</Text>
                      <Text size="sm" fw={500}>{shipmentData.packageCount}</Text>
                    </Group>
                  </Stack>
                </Paper>

                {/* Destination */}
                <DestinationAddress destination={shipmentData.destination} />

                {/* Cost Summary */}
                <CostSummary cost={shipmentData.cost} />

                {/* Shipment Summary */}
                <ShipmentSummary
                  totalWeight={shipmentData.totalWeight}
                  totalValue={shipmentData.totalValue}
                  packageCount={shipmentData.packageCount}
                />
              </Stack>
            </Grid.Col>
          </Grid>
        </Box>

        {/* Desktop Back Button */}
        <Box mt="xl" display={{ base: 'none', sm: 'block' }}>
          <Group justify="space-between">
            <Button
              component={Link}
              to="/shipments"
              leftSection={<IconArrowLeft size={16} />}
              variant="light"
              size="md"
            >
              Back to Shipments
            </Button>

            {/* Additional actions could go here */}
            {/* <Group gap="sm">
              <Button variant="outline" size="md">
                Print Details
              </Button>
              <Button variant="outline" size="md">
                Download Invoice
              </Button>
            </Group> */}
          </Group>
        </Box>
      </Container>

      {/* Scroll to Top Button */}
      <Affix position={{ bottom: 20, right: 20 }}>
        <Transition transition="slide-up" mounted={scroll.y > 200}>
          {(transitionStyles) => (
            <ActionIcon
              size="lg"
              variant="filled"
              radius="xl"
              style={transitionStyles}
              onClick={() => scrollTo({ y: 0 })}
            >
              <IconArrowUp size={18} />
            </ActionIcon>
          )}
        </Transition>
      </Affix>

    </>
  );
};

export default ShipmentDetail;
